/**
 * Custom staff role switches (#6135, plan
 * docs/plans/2026-10-27-1252-feat-custom-staff-roles-plan.md R3, R4, R6, KTD1,
 * KTD2).
 *
 * A custom role is a row in org_staff_roles with role_name = 'custom', a
 * gym-chosen display_name, thirteen `perm_<key>` boolean columns and a
 * `team_scope`. Built-in roles (owner, admin, coach, front_desk,
 * read_only_staff) never read these columns: their powers come from their role
 * name, exactly as before, and the database CHECK
 * `org_staff_roles_builtin_no_switches` keeps every switch off on their rows.
 *
 * Labels and descriptions are the approved copy from
 * docs/plans/2026-10-27-1252-custom-staff-roles-demo.html (Geng, 2026-10-30).
 *
 * staffAccessAllows is the one decision every gate uses: the server
 * (requireStaffAccess), the screens (useStaffAccess, ProtectedPage, the nav)
 * and, in SQL, staff_has_permission follow the same rule.
 */

import { CUSTOM_STAFF_ROLE_NAME } from '@/utils/constants/roles';

// The switch list, the team scope rule and the R13 sentence live in
// shared/staff-access so the phone app reads the same ones (PR 11, U6).
import {
  STAFF_PERMISSION_KEYS,
  STAFF_TEAM_SCOPES,
  TEAM_SCOPED_PERMISSION_KEYS,
  type StaffPermissionKey,
  type StaffPermissionSet,
  type StaffTeamScope,
} from '@cheervue/staff-access';

export {
  STAFF_NO_ACCESS_MESSAGE,
  STAFF_PERMISSION_KEYS,
  STAFF_TEAM_SCOPES,
  TEAM_SCOPED_PERMISSION_KEYS,
  type StaffPermissionKey,
  type StaffPermissionSet,
  type StaffTeamScope,
} from '@cheervue/staff-access';

/** The org_staff_roles column that stores a switch. */
export function staffPermissionColumn(key: StaffPermissionKey): `perm_${StaffPermissionKey}` {
  return `perm_${key}`;
}

/** Comma-separated column list for a PostgREST select of every switch. */
export const STAFF_PERMISSION_COLUMNS_SELECT = STAFF_PERMISSION_KEYS.map(staffPermissionColumn).join(', ');

/**
 * The assistant's answer to a money totals question for a custom role without
 * See money (R14, R16). The "What staff see" preview quotes this same text.
 */
export const ASSISTANT_MONEY_REFUSAL = 'Money totals are handled by the gym owner or an administrator.';

export const STAFF_PERMISSION_DETAILS: Readonly<
  Record<StaffPermissionKey, { label: string; description: string }>
> = {
  teams_athletes: {
    label: 'Teams & athletes',
    description: 'Rosters, athlete records, placements, attendance, evaluations, medical and incident notes.',
  },
  messages: {
    label: 'Messages',
    description: 'Send announcements and messages to families and staff. Sees only the team chats they were added to.',
  },
  staff: {
    label: 'Staff',
    description: 'Invite, pause and remove Coaches and Staff Members, only at their own locations. Cannot change Owners, Administrators or Front Desk.',
  },
  classes_schedule: {
    label: 'Classes & schedule',
    description: 'Classes, private lessons, the calendar, closings, waitlists and events. Not their prices.',
  },
  prices: {
    label: 'Prices',
    description: 'Set class, private lesson, open gym, ticket and merch prices. Never discounts or fees.',
  },
  time_hours: {
    label: 'Time & hours',
    description: "Staff schedules, PINs, kiosks, and approving other people's hours. Never pay.",
  },
  reports: {
    label: 'Reports',
    description: 'Reports with no money in them: roster, attendance, waivers, message reach.',
  },
  gym_settings: {
    label: 'Gym settings',
    description: 'Brand, kiosk, automations, policies and other settings with no money in them.',
  },
  take_payments: {
    label: 'Take payments',
    description: 'Use the register, record a payment, send a payment link, sell walk-ins. Sees only the sale or the one family in front of them.',
  },
  refunds_discounts: {
    label: 'Refunds & discounts',
    description: 'Refunds, credits, voids, discounts, coupons and custom charges. Turns on See money.',
  },
  see_money: {
    label: 'See money',
    description: 'Finance pages, revenue, all bills and payments, sales history, chargebacks and fees.',
  },
  pay_payroll: {
    label: 'Pay & payroll',
    description: 'Staff pay rates and the payroll export.',
  },
  payouts_billing: {
    label: 'Payouts & billing settings',
    description: 'Bank and payout accounts, tax, fees, billing policies, auto-invoicing and the CheerVue plan.',
  },
};

/** The editor's three groups, in display order (R3). */
export const STAFF_PERMISSION_GROUPS: ReadonlyArray<{
  title: string;
  keys: readonly StaffPermissionKey[];
}> = [
  { title: 'People and teams', keys: ['teams_athletes', 'messages', 'staff'] },
  {
    title: 'Running the gym',
    keys: ['classes_schedule', 'prices', 'time_hours', 'reports', 'gym_settings'],
  },
  {
    title: 'Money',
    keys: ['take_payments', 'refunds_discounts', 'see_money', 'pay_payroll', 'payouts_billing'],
  },
];

/**
 * R4: turning a switch on turns these on too, and they stay on while it is.
 * The database CHECK `org_staff_roles_refunds_need_see_money` enforces it.
 * Take payments deliberately implies nothing.
 */
export const STAFF_PERMISSION_IMPLIES: Readonly<
  Partial<Record<StaffPermissionKey, readonly StaffPermissionKey[]>>
> = {
  refunds_discounts: ['see_money'],
};

export const STAFF_TEAM_SCOPE_LABELS: Readonly<Record<StaffTeamScope, string>> = {
  all: 'All teams',
  assigned: 'Only their teams',
};

export function emptyStaffPermissionSet(): StaffPermissionSet {
  return Object.fromEntries(STAFF_PERMISSION_KEYS.map((key) => [key, false])) as Record<
    StaffPermissionKey,
    boolean
  >;
}

/**
 * Read the switches off an org_staff_roles row (or PostgREST embed). Anything
 * other than a literal `true` reads as off.
 */
export function staffPermissionSetFromRow(
  row: Partial<Record<string, unknown>> | null | undefined
): StaffPermissionSet {
  return Object.fromEntries(
    STAFF_PERMISSION_KEYS.map((key) => [key, row?.[staffPermissionColumn(key)] === true])
  ) as Record<StaffPermissionKey, boolean>;
}

/** `assigned` only when the row says so; anything else is the default, `all`. */
export function staffTeamScopeFromRow(
  row: Partial<Record<string, unknown>> | null | undefined
): StaffTeamScope {
  return row?.team_scope === 'assigned' ? 'assigned' : 'all';
}

/**
 * The role name a staff check reads for a signed-in user: the active built-in
 * role, or 'custom' for a custom role seat (whose activeRole is null and whose
 * role row is on `customRole`). Null when neither is set.
 */
export function activeStaffRoleName(
  user: { activeRole?: string | null; customRole?: unknown } | null | undefined
): string | null {
  if (!user) return null;
  if (user.activeRole) return user.activeRole;
  return user.customRole ? CUSTOM_STAFF_ROLE_NAME : null;
}

export interface StaffAccessRequirement {
  /** Built-in role names that pass, exactly as the gate allows them today. */
  roles?: readonly string[];
  /**
   * A custom role passes only when EVERY key here is on. `null` or omitted
   * means no custom role passes on this list (a built-in-only gate).
   */
  permissions?: readonly StaffPermissionKey[] | null;
  /** A custom role passes only when AT LEAST ONE key here is on. */
  anyOf?: readonly StaffPermissionKey[];
}

/**
 * KTD2's dual check. A built-in role passes when its name is in `roles`; its
 * switches are never read. A custom role passes only when the requirement
 * names at least one switch and every condition holds (all of `permissions`,
 * one of `anyOf`). Any other role name — unknown, legacy, empty — is refused.
 * 'custom' in `roles` admits nobody: a custom role passes only by its switches.
 */
export function staffAccessAllows(
  roleName: string | null | undefined,
  permissionSet: StaffPermissionSet | null | undefined,
  requirement: StaffAccessRequirement
): boolean {
  if (!roleName) return false;
  if (roleName !== CUSTOM_STAFF_ROLE_NAME && requirement.roles?.includes(roleName)) return true;
  if (roleName !== CUSTOM_STAFF_ROLE_NAME || !permissionSet) return false;

  const allOf = requirement.permissions ?? [];
  const anyOf = requirement.anyOf ?? [];
  if (allOf.length === 0 && anyOf.length === 0) return false;
  if (!allOf.every((key) => permissionSet[key] === true)) return false;
  if (anyOf.length > 0 && !anyOf.some((key) => permissionSet[key] === true)) return false;
  return true;
}

/** A custom role seat as the screens hold it (UserRoleContext's `customRole`). */
export interface CustomRoleSeat {
  permissions: StaffPermissionSet;
  teamScope?: StaffTeamScope;
}

export interface CustomRoleRequirement {
  /** Every key here must be on. */
  permissions?: readonly StaffPermissionKey[];
  /** At least one key here must be on. */
  anyOf?: readonly StaffPermissionKey[];
  /**
   * Gym-wide work: the role must also be "All teams". The server refuses it
   * to "Only their teams" (refuseCustomTeamScoped), so the screen hides it too.
   */
  allTeams?: boolean;
}

/**
 * The custom half of KTD2's dual check, for a screen that already decides
 * built-in roles by role name (#6135, U10 client sweep). Write it beside the
 * built-in check — `canX(user.activeRole) || customRoleAllows(user, {...})` —
 * so a built-in role's answer cannot change. False for every built-in seat,
 * for a seat still loading, and for a requirement that names no switch.
 */
export function customRoleAllows(
  user: { customRole?: CustomRoleSeat | null } | null | undefined,
  requirement: CustomRoleRequirement
): boolean {
  const seat = user?.customRole;
  if (!seat) return false;
  // A missing scope reads as "All teams", as staffTeamScopeFromRow and the menu read it.
  if (requirement.allTeams && (seat.teamScope ?? 'all') !== 'all') return false;
  return staffAccessAllows(CUSTOM_STAFF_ROLE_NAME, seat.permissions, {
    permissions: requirement.permissions ?? null,
    anyOf: requirement.anyOf,
  });
}
